import type { AxiosRequestConfig } from 'axios';
import {
  MOBILE_PAIRING_REFUSALS,
  type IMobilePairingRequestCreated,
  type IMobilePairingRequestView,
  type IReauthGranted,
  type MobilePairingStateType,
} from '@featit/shared/mobile-pairing';
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser';
import { ApiService } from './ApiService';
import { subscribeOnRealtimeSocket } from './realtimeSocket';

/**
 * A refusal's answer to "is this a wrong password", not "is the session over" (F-0332).
 *
 * The step-up routes are signed in, and a 401 from them means the password or passkey was refused —
 * which the response interceptor would otherwise read as an expired token, refresh, and send again:
 * one mistyped password counted twice against the five-a-minute limit. Marking the request as
 * already retried is the interceptor's own switch for "do not renew for this one".
 */
const ANSWERED_BY_ITS_OWN_401 = { _retry: true } as AxiosRequestConfig;

/** What `approve` answers when the approver has to prove they are present first. */
export interface IReauthRequired {
  passkey: boolean;
  password: boolean;
}

/** The step-ups a refusal offers, or null when it is not the recent-auth refusal. */
export function reauthRequired(error: unknown): IReauthRequired | null {
  const data = (
    error as {
      response?: { status?: number; data?: { code?: unknown; methods?: Partial<IReauthRequired> } };
    }
  )?.response;
  if (data?.status !== 403 || data.data?.code !== MOBILE_PAIRING_REFUSALS.REAUTH_REQUIRED) {
    return null;
  }
  return {
    passkey: Boolean(data.data.methods?.passkey),
    password: Boolean(data.data.methods?.password),
  };
}

/** Whether a refusal says the request is gone: past its two minutes, or replaced by a newer one. */
export function pairingExpired(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })
    ?.response;
  return response?.status === 410 || response?.data?.code === MOBILE_PAIRING_REFUSALS.EXPIRED;
}

/**
 * Connecting a mobile device by QR code and a confirmation, as the approver (F-0332, ADR-151).
 * The new device's half — register, status, complete — belongs to the apps, never to this page.
 */
class PairingApiService extends ApiService {
  create(): Promise<IMobilePairingRequestCreated> {
    return this.post('/auth/pair/requests', {});
  }

  view(requestId: string): Promise<IMobilePairingRequestView> {
    return this.get(`/auth/pair/requests/${encodeURIComponent(requestId)}`);
  }

  approve(requestId: string, reauthToken?: string | null): Promise<IMobilePairingRequestView> {
    return this.post(
      `/auth/pair/requests/${encodeURIComponent(requestId)}/approve`,
      reauthToken ? { reauthToken } : {},
    );
  }

  deny(requestId: string): Promise<IMobilePairingRequestView> {
    return this.post(`/auth/pair/requests/${encodeURIComponent(requestId)}/deny`, {});
  }

  reauthWithPassword(password: string): Promise<IReauthGranted> {
    return this.post('/auth/reauth/password', { password }, ANSWERED_BY_ITS_OWN_401);
  }

  reauthWithPasskey(
    challengeId: string,
    response: AuthenticationResponseJSON,
  ): Promise<IReauthGranted> {
    return this.post('/auth/reauth/passkey', { challengeId, response }, ANSWERED_BY_ITS_OWN_401);
  }

  /**
   * The request moved — a device registered, or it completed. A signal on the tab's one socket;
   * the caller reads the request again. Returns the unsubscribe.
   */
  subscribe(
    requestId: string,
    onChange: (state: MobilePairingStateType) => void,
    onError: (error: unknown) => void,
  ): () => void {
    return subscribeOnRealtimeSocket<{
      pairingRequestUpdated: { requestId: string; state: MobilePairingStateType };
    }>(
      {
        query: `subscription PairingRequestUpdated($id: ID!) {
          pairingRequestUpdated(id: $id) { requestId state at }
        }`,
        variables: { id: requestId },
      },
      {
        next: ({ data }) => {
          if (data?.pairingRequestUpdated) onChange(data.pairingRequestUpdated.state);
        },
        error: onError,
        complete: () => onError(new Error('Pairing updates ended.')),
      },
    );
  }
}

export const pairingApi = new PairingApiService();
